import React from 'react';
import { Phone, MessageCircle, Send } from 'lucide-react';
import { businessInfo, heroImage } from '../data/mock';

const Hero = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${businessInfo.phoneRaw}`, '_blank');
  };

  const handleViber = () => {
    window.open(`viber://chat?number=${businessInfo.phoneRaw}`, '_blank');
  };

  const handleCall = () => {
    window.location.href = `tel:${businessInfo.phoneRaw}`;
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/70" />
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-white space-y-8">
            <div className="space-y-4">
              <div>
                <span className="inline-block bg-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                  Preko 50,000 zadovoljnih klijenata
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Majstorska popravka
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-blue-500">
                  vaših omiljenih predmeta
                </span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Profesionalna radionica sa dugogodišnjim iskustvom u popravci obuće, 
                tašni, kofera i proizvodnji kožne galanterije. Kvalitet i preciznost u svakom detalju.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 py-6">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-teal-400">20,000+</div>
                <div className="text-sm text-gray-400 mt-1">Obuća</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-blue-400">20,000+</div>
                <div className="text-sm text-gray-400 mt-1">Tašni</div>
              </div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-cyan-400">10,000+</div>
                <div className="text-sm text-gray-400 mt-1">Kofera</div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="space-y-4">
              <p className="text-lg font-semibold text-gray-300">Kontaktirajte nas odmah:</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleCall}
                  className="group flex items-center justify-center space-x-3 bg-teal-600 text-white px-8 py-4 rounded-xl hover:bg-teal-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 font-semibold"
                >
                  <Phone className="w-5 h-5 group-hover:rotate-12 transition-transform" />
                  <span>Pozovite nas</span>
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="group flex items-center justify-center space-x-3 bg-green-600 text-white px-8 py-4 rounded-xl hover:bg-green-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 font-semibold"
                >
                  <MessageCircle className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>WhatsApp</span>
                </button>
                <button
                  onClick={handleViber}
                  className="group flex items-center justify-center space-x-3 bg-purple-600 text-white px-8 py-4 rounded-xl hover:bg-purple-700 transition-all shadow-xl hover:shadow-2xl hover:scale-105 font-semibold"
                >
                  <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  <span>Viber</span>
                </button>
              </div>
            </div>

            {/* Contact Info */}
            <div className="pt-6 border-t border-gray-700 space-y-2">
              <p className="text-gray-400">
                <span className="font-semibold text-white">Telefon:</span> {businessInfo.phone}
              </p>
              <p className="text-gray-400">
                <span className="font-semibold text-white">Radno vreme:</span> {businessInfo.workingHours.weekdays}
              </p>
              <p className="text-gray-400">
                <span className="font-semibold text-white">Adresa:</span> {businessInfo.address.full}
              </p>
            </div>
          </div>

          {/* Image Card */}
          <div className="hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-teal-500 via-blue-500 to-cyan-500 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000"></div>
              <div className="relative bg-gray-800 rounded-2xl p-2">
                <img 
                  src={heroImage} 
                  alt="Majstorska radionica" 
                  className="w-full h-[600px] object-cover rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
